// ==========================================================
// app/confidentialite.tsx — Politique de Confidentialité v1.3
// ==========================================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import LegalDocumentScreen, { LegalSection } from '../components/LegalDocumentScreen';

const VIOLET_LIGHT = '#9B30E8';
const VIOLET_PALE  = '#C47EFF';
const GOLD         = '#F5A623';
const SUCCESS      = '#00A86B';
const DANGER       = '#E8003D';
const BLUE         = '#3B82F6';

function useSections(t: (key: string, opts?: any) => any): LegalSection[] {
  return [
    {
      id: 'intro', num: '01', icon: 'information-circle-outline', color: VIOLET_PALE,
      title: t('confidentialite.sections.intro.title'),
      content: [
        { type: 'paragraph', text: t('confidentialite.sections.intro.p1') },
        { type: 'paragraph', text: t('confidentialite.sections.intro.p2') },
        { type: 'highlight', text: t('confidentialite.sections.intro.highlight1'), icon: 'checkmark-circle' },
      ],
    },
    {
      id: 'responsable', num: '02', icon: 'person-circle-outline', color: GOLD,
      title: t('confidentialite.sections.responsable.title'),
      content: [
        { type: 'paragraph', text: t('confidentialite.sections.responsable.p1') },
        { type: 'highlight', text: 'harmonia@harmoniaworld.world', icon: 'mail' },
        { type: 'paragraph', text: t('confidentialite.sections.responsable.p2') },
      ],
    },
    {
      id: 'donnees', num: '03', icon: 'server-outline', color: SUCCESS,
      title: t('confidentialite.sections.donnees.title'),
      content: [
        { type: 'subtitle', text: t('confidentialite.sections.donnees.sub1') },
        { type: 'bullet', items: t('confidentialite.sections.donnees.bullets1', { returnObjects: true }) },
        { type: 'subtitle', text: t('confidentialite.sections.donnees.sub2') },
        { type: 'bullet', items: t('confidentialite.sections.donnees.bullets2', { returnObjects: true }) },
        { type: 'subtitle', text: t('confidentialite.sections.donnees.sub3') },
        { type: 'paragraph', text: t('confidentialite.sections.donnees.p1') },
        { type: 'subtitle', text: t('confidentialite.sections.donnees.sub4') },
        { type: 'paragraph', text: t('confidentialite.sections.donnees.p2') },
        { type: 'subtitle', text: t('confidentialite.sections.donnees.sub5') },
        { type: 'paragraph', text: t('confidentialite.sections.donnees.p3') },
        { type: 'subtitle', text: t('confidentialite.sections.donnees.sub6') },
        { type: 'highlight', text: t('confidentialite.sections.donnees.highlight1'), icon: 'storefront' },
      ],
    },
    {
      id: 'utilisation', num: '04', icon: 'analytics-outline', color: VIOLET_LIGHT,
      title: t('confidentialite.sections.utilisation.title'),
      content: [
        { type: 'bullet', items: t('confidentialite.sections.utilisation.bullets1', { returnObjects: true }) },
        { type: 'highlight', text: t('confidentialite.sections.utilisation.highlight1'), icon: 'shield-checkmark' },
      ],
    },
    {
      id: 'concours', num: '05', icon: 'trophy-outline', color: GOLD,
      title: t('confidentialite.sections.concours.title'),
      content: [
        { type: 'paragraph', text: t('confidentialite.sections.concours.p1') },
        { type: 'highlight', text: t('confidentialite.sections.concours.highlight1'), icon: 'lock-closed' },
        { type: 'paragraph', text: t('confidentialite.sections.concours.p2') },
        { type: 'subtitle', text: t('confidentialite.sections.concours.sub1') },
        { type: 'paragraph', text: t('confidentialite.sections.concours.p3') },
      ],
    },
    {
      id: 'publicite', num: '06', icon: 'megaphone-outline', color: '#E67E22',
      title: t('confidentialite.sections.publicite.title'),
      content: [
        { type: 'paragraph', text: t('confidentialite.sections.publicite.p1') },
        { type: 'highlight', text: t('confidentialite.sections.publicite.highlight1'), icon: 'ban' },
      ],
    },
    {
      id: 'securite', num: '07', icon: 'shield-checkmark-outline', color: SUCCESS,
      title: t('confidentialite.sections.securite.title'),
      content: [
        { type: 'paragraph', text: t('confidentialite.sections.securite.p1') },
        { type: 'bullet', items: t('confidentialite.sections.securite.bullets1', { returnObjects: true }) },
        { type: 'subtitle', text: t('confidentialite.sections.securite.sub1') },
        { type: 'paragraph', text: t('confidentialite.sections.securite.p2') },
      ],
    },
    {
      id: 'partage', num: '08', icon: 'share-social-outline', color: DANGER,
      title: t('confidentialite.sections.partage.title'),
      content: [
        { type: 'highlight', text: t('confidentialite.sections.partage.highlight1'), icon: 'shield-checkmark' },
        { type: 'bullet', items: t('confidentialite.sections.partage.bullets1', { returnObjects: true }) },
        { type: 'paragraph', text: t('confidentialite.sections.partage.p1') },
      ],
    },
    {
      id: 'mineurs', num: '09', icon: 'people-outline', color: BLUE,
      title: t('confidentialite.sections.mineurs.title'),
      content: [
        { type: 'paragraph', text: t('confidentialite.sections.mineurs.p1') },
        { type: 'highlight', text: t('confidentialite.sections.mineurs.highlight1'), icon: 'people' },
      ],
    },
    {
      id: 'droits', num: '10', icon: 'hand-left-outline', color: VIOLET_LIGHT,
      title: t('confidentialite.sections.droits.title'),
      content: [
        { type: 'bullet', items: t('confidentialite.sections.droits.bullets1', { returnObjects: true }) },
        { type: 'highlight', text: t('confidentialite.sections.droits.highlight1'), icon: 'mail' },
      ],
    },
    {
      id: 'conservation', num: '11', icon: 'time-outline', color: GOLD,
      title: t('confidentialite.sections.conservation.title'),
      content: [
        { type: 'paragraph', text: t('confidentialite.sections.conservation.p1') },
        { type: 'bullet', items: t('confidentialite.sections.conservation.bullets1', { returnObjects: true }) },
      ],
    },
    {
      id: 'cookies', num: '12', icon: 'settings-outline', color: '#8B5CF6',
      title: t('confidentialite.sections.cookies.title'),
      content: [
        { type: 'highlight', text: t('confidentialite.sections.cookies.highlight1'), icon: 'ban' },
        { type: 'paragraph', text: t('confidentialite.sections.cookies.p1') },
      ],
    },
    {
      id: 'responsabilite', num: '13', icon: 'alert-circle-outline', color: DANGER,
      title: t('confidentialite.sections.responsabilite.title'),
      content: [
        { type: 'paragraph', text: t('confidentialite.sections.responsabilite.p1') },
        { type: 'paragraph', text: t('confidentialite.sections.responsabilite.p2') },
      ],
    },
    {
      id: 'modifications', num: '14', icon: 'refresh-outline', color: BLUE,
      title: t('confidentialite.sections.modifications.title'),
      content: [
        { type: 'paragraph', text: t('confidentialite.sections.modifications.p1') },
        { type: 'highlight', text: t('confidentialite.sections.modifications.highlight1'), icon: 'checkmark-circle' },
      ],
    },
    {
      id: 'contact', num: '15', icon: 'mail-outline', color: SUCCESS,
      title: t('confidentialite.sections.contact.title'),
      content: [
        { type: 'highlight', text: 'harmonia@harmoniaworld.world', icon: 'mail' },
        { type: 'paragraph', text: t('confidentialite.sections.contact.p1') },
      ],
    },
  ];
}

export default function ConfidentialiteScreen() {
  const { t } = useTranslation();
  const SECTIONS = useSections(t);

  return (
    <LegalDocumentScreen
      title={t('confidentialite.title')}
      version="v1.3"
      sections={SECTIONS}
      currentRoute="/confidentialite"
      stats={[
        { value: '15', label: t('confidentialite.stats.sections') },
        { isIcon: true, iconName: 'shield-checkmark', iconColor: '#FFC947', label: t('confidentialite.stats.gdpr') },
        { isIcon: true, iconName: 'lock-closed',      iconColor: '#FFC947', label: t('confidentialite.stats.secure') },
      ]}
    />
  );
}
